import { z } from 'zod';

import { PaymentsBusinessCDDIdBucketSchema } from '@fragment/buckets/paymentsBusinessCDDId';
import { PaymentsLegalEntityIdBucketSchema } from '@fragment/buckets/paymentsLegalEntityId';
import { SafeStringSchema } from '@fragment/types';

import { entityDocSchema } from '../shared/entity';

export const PaymentsPlatformEntityDocSchema = z
  .object({
    entity: z.literal('PaymentsPlatformEntity'),
    workspaceId: z.string(),
    businessEntities: z.array(PaymentsLegalEntityIdBucketSchema),
  })
  .merge(entityDocSchema);

export type PaymentsPlatformEntityDoc = z.infer<
  typeof PaymentsPlatformEntityDocSchema
>;

export const LegalEntityStatusSchema = z.enum([
  'pending-submission',
  'pending-approval',
  'approved',
  'manual-review',
  'rejected',
]);

export type LegalEntityStatus = z.infer<typeof LegalEntityStatusSchema>;

export const LegalEntityTypeSchema = z.enum(['business', 'individual']);

export const IndividualOnboardingRoleSchema = z.enum([
  'Controller',
  'Owner',
  'Both',
]);
export type IndividualOnboardingRole = z.infer<
  typeof IndividualOnboardingRoleSchema
>;

export const LegalEntityHistorySchema = z.discriminatedUnion('event', [
  z.object({
    event: z.literal('started'),
    dateTime: z.string(),
  }),
  z.object({
    event: z.literal('submitted'),
    oscilarRecordId: z.string(),
    dateTime: z.string(),
  }),
  z.object({
    event: z.literal('approved'),
    oscilarCaseId: z.string(),
    dateTime: z.string(),
  }),
  z.object({
    event: z.literal('manual-review'),
    oscilarCaseId: z.string(),
    dateTime: z.string(),
  }),
  z.object({
    event: z.literal('rejected'),
    oscilarCaseId: z.string(),
    dateTime: z.string(),
  }),
]);

export type LegalEntityHistory = z.infer<typeof LegalEntityHistorySchema>;

export const BusinessCDDBuyerSellerConnectionSchema = z.enum([
  'same-sellers-consistently',
  'many-different-sellers',
]);

export type BusinessCDDBuyerSellerConnection = z.infer<
  typeof BusinessCDDBuyerSellerConnectionSchema
>;

export const BusinessCDDLicenseSchema = z.object({
  type: z.string(),
  number: z.string(),
});

export type BusinessCDDLicense = z.infer<typeof BusinessCDDLicenseSchema>;

export const BusinessCDDDocSchema = z
  .object({
    entity: z.literal('BusinessCDD'),
    workspaceId: SafeStringSchema,
    instance: z.number(),
    businessEntityId: SafeStringSchema.optional(),
    structure: z.string().optional(),
    description: z.string().optional(),
    isRegulatedIndustry: z.boolean().optional(),
    holdFinancialLicenses: z.boolean().optional(),
    licenses: z.array(BusinessCDDLicenseSchema).optional(),
    hasUndergoneSignificantChanges: z.boolean().optional(),
    changes: z.string().optional(),
    strategicPartners: z.string().optional(),
    industryVertical: z.string().optional(),
    buyerSellerConnection: BusinessCDDBuyerSellerConnectionSchema.optional(),
    sellersAsIntermediaries: z.boolean().optional(),
    allowsCrossBorder: z.boolean().optional(),
    countriesOfOperation: z.array(z.string()).optional(),
    beneficialOwners: z.array(PaymentsLegalEntityIdBucketSchema).optional(),
    termsAgreedAt: z.string().optional(),
    termsUrl: z.string().optional(),
    clientIp: z.string().optional(),
  })
  .merge(entityDocSchema);

export type BusinessCDDDoc = z.infer<typeof BusinessCDDDocSchema>;

export const LegalEntityDocSchema = z
  .object({
    entity: z.literal('LegalEntity'),
    entityId: SafeStringSchema,
    status: LegalEntityStatusSchema,
    history: z.array(LegalEntityHistorySchema),
    type: LegalEntityTypeSchema.optional(),
    controllerIds: z.array(PaymentsLegalEntityIdBucketSchema).optional(),
    ownerIds: z.array(PaymentsLegalEntityIdBucketSchema).optional(),
    piiDataTokenId: z.string().optional(),
    businessCddIds: z.array(PaymentsBusinessCDDIdBucketSchema).optional(),
    userId: z.string().optional(),
  })
  .merge(entityDocSchema);

export type LegalEntityDoc = z.infer<typeof LegalEntityDocSchema>;
